// =============================================================================
// SHANIZ GAMING ADMIN - GLOBAL SEARCH DROPDOWN
// =============================================================================
// Icon-button + Popover. Debounced 300ms. Three parallel queries
// (users / transactions / tickets) surface up to 5 results each.
// =============================================================================

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { userService, transactionService, supportService } from '../services/api';

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Debounce 300ms — plain setTimeout, no lodash.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const enabled = debounced.length >= 2;

  const usersQ = useQuery({
    queryKey: ['global-search', 'users', debounced],
    queryFn: () => userService.search(debounced, 5),
    enabled,
  });

  const txQ = useQuery({
    queryKey: ['global-search', 'transactions', debounced],
    queryFn: () => transactionService.search(debounced, 5),
    enabled,
  });

  const ticketsQ = useQuery({
    queryKey: ['global-search', 'tickets', debounced],
    queryFn: () => supportService.search(debounced, 5),
    enabled,
  });

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    // Autofocus the input shortly after the popover mounts.
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const pick = (path: string) => {
    navigate(path);
    handleClose();
  };

  const anyLoading =
    enabled && (usersQ.isLoading || txQ.isLoading || ticketsQ.isLoading);
  const users = usersQ.data ?? [];
  const transactions = txQ.data ?? [];
  const tickets = ticketsQ.data ?? [];
  const noResults =
    enabled &&
    !anyLoading &&
    users.length === 0 &&
    transactions.length === 0 &&
    tickets.length === 0;

  return (
    <>
      <Tooltip title="Search">
        <IconButton
          aria-label="Open global search"
          color="inherit"
          onClick={handleOpen}
        >
          <SearchIcon />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 460,
            maxHeight: 560,
            bgcolor: '#141414',
            color: '#F5F5F5',
            border: '1px solid #262626',
            mt: 0.5,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 1,
            borderBottom: '1px solid #262626',
            bgcolor: '#1A1A1A',
          }}
        >
          <SearchIcon fontSize="small" sx={{ color: '#A3A3A3' }} />
          <InputBase
            inputRef={inputRef}
            autoFocus
            placeholder="Search users, transactions, tickets…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ ml: 1, color: '#F5F5F5', flex: 1, fontSize: 14 }}
          />
        </Box>

        <Box sx={{ p: 1.5, overflowY: 'auto', maxHeight: 480 }}>
          {!enabled && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
              Type at least 2 characters to search…
            </Typography>
          )}

          {enabled && (
            <>
              <SearchSection
                title="Users"
                loading={usersQ.isLoading}
                items={users.map((u) => ({
                  id: u.id,
                  primary: u.username || u.email || u.id,
                  secondary: u.email || '',
                }))}
                onPick={(id) => pick(`/users/${id}`)}
              />
              <Divider sx={{ my: 1, borderColor: '#262626' }} />
              <SearchSection
                title="Transactions"
                loading={txQ.isLoading}
                items={transactions.map((t: any) => ({
                  id: t.id,
                  primary: t.reference || t.id,
                  secondary:
                    (t.amount != null ? `${t.currency || ''} ${t.amount}` : '') +
                    (t.status ? ` · ${t.status}` : ''),
                }))}
                // Transactions list route has no :id detail; go to the list.
                onPick={() => pick('/transactions')}
              />
              <Divider sx={{ my: 1, borderColor: '#262626' }} />
              <SearchSection
                title="Tickets"
                loading={ticketsQ.isLoading}
                items={tickets.map((t) => ({
                  id: t.id,
                  primary: t.subject,
                  secondary: `${t.status}${t.user?.username ? ` · ${t.user.username}` : ''}`,
                }))}
                onPick={(id) => pick(`/support/${id}`)}
              />

              {noResults && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
                  {`No matches for «${debounced}»`}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

interface SectionItem {
  id: string;
  primary: string;
  secondary?: string;
}

const SearchSection: React.FC<{
  title: string;
  loading: boolean;
  items: SectionItem[];
  onPick: (id: string) => void;
}> = ({ title, loading, items, onPick }) => {
  return (
    <Box sx={{ mb: 0.5 }}>
      <Typography
        variant="caption"
        sx={{
          px: 1,
          color: '#737373',
          fontWeight: 700,
          letterSpacing: 1,
          display: 'block',
        }}
      >
        {title.toUpperCase()}
      </Typography>
      {loading ? (
        <Box sx={{ px: 1, py: 0.5 }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ py: 0.75 }}>
              <Skeleton variant="text" width="60%" sx={{ bgcolor: '#262626' }} />
              <Skeleton variant="text" width="40%" sx={{ bgcolor: '#1f1f1f' }} />
            </Box>
          ))}
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5, display: 'block' }}>
          —
        </Typography>
      ) : (
        <List dense sx={{ py: 0 }}>
          {items.slice(0, 5).map((it) => (
            <ListItem key={it.id} disablePadding>
              <ListItemButton
                onClick={() => onPick(it.id)}
                sx={{ borderRadius: 1, '&:hover': { bgcolor: '#1f1f1f' } }}
              >
                <ListItemText
                  primary={it.primary}
                  secondary={it.secondary || undefined}
                  primaryTypographyProps={{ fontSize: 14, color: '#F5F5F5' }}
                  secondaryTypographyProps={{ fontSize: 12, color: '#A3A3A3' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default GlobalSearch;
